import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppStore } from './store'
import { buildExplorerSearch, readViewport } from './urlState'

/**
 * Hydrates viewer state from the current URL on mount, then writes
 * subsequent state changes back to the URL (replace, so the back-button
 * still works for real navigation).
 *
 * Notes:
 * - Only runs when `enabled` is true. Standalone /zarr-explorer route
 *   passes true; the collection-tab embed passes false because that URL
 *   belongs to the collection page, not the viewer.
 * - `collection` param is honored only in `global` scope; the tab-embed
 *   locks the dataset via prop, so a URL `?collection=` would fight it.
 */
export function useUrlSync(enabled: boolean) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const hydrated = useRef(false)

  const scope = useAppStore((s) => s.scope)
  const datasetId = useAppStore((s) => s.datasetId)
  const datasetState = useAppStore((s) => s.datasetState) as Record<string, unknown>
  const clim = useAppStore((s) => s.clim)
  const colormap = useAppStore((s) => s.colormap)
  const opacity = useAppStore((s) => s.opacity)
  const timeMeanAutoClim = useAppStore((s) => s.timeMeanAutoClim)
  const mapInstance = useAppStore((s) => s.mapInstance)

  const setDatasetId = useAppStore((s) => s.setDatasetId)
  const setClim = useAppStore((s) => s.setClim)
  const setColormap = useAppStore((s) => s.setColormap)
  const setOpacity = useAppStore((s) => s.setOpacity)
  const setTimeMeanAutoClim = useAppStore((s) => s.setTimeMeanAutoClim)
  const setActiveDatasetState = useAppStore((s) => s.setActiveDatasetState)

  // 1. HYDRATE on mount (runs once per enabled).
  useEffect(() => {
    if (!enabled || hydrated.current) return
    hydrated.current = true

    const p = searchParams
    const collection = p.get('collection')
    if (collection && scope !== 'collection') setDatasetId(collection)

    const cmap = p.get('cmap')
    if (cmap) setColormap(cmap)

    const vmin = p.get('vmin')
    const vmax = p.get('vmax')
    const lo = vmin !== null ? parseFloat(vmin) : NaN
    const hi = vmax !== null ? parseFloat(vmax) : NaN
    if (Number.isFinite(lo) && Number.isFinite(hi)) setClim([lo, hi])

    const op = p.get('opacity')
    if (op !== null) {
      const v = parseFloat(op)
      if (Number.isFinite(v)) setOpacity(v)
    }

    if (p.get('autoscale') === '1') setTimeMeanAutoClim(true)

    const dsPatch: Record<string, unknown> = {}
    const variable = p.get('variable')
    if (variable) dsPatch.variable = variable
    const qa = p.get('qa')
    if (qa !== null) {
      const q = parseInt(qa, 10)
      if (Number.isFinite(q)) dsPatch.qaIndex = q
    }
    const t = p.get('t')
    if (t !== null) {
      const ti = parseInt(t, 10)
      if (Number.isFinite(ti)) dsPatch.time = ti
    }
    if (Object.keys(dsPatch).length) setActiveDatasetState(dsPatch)

    // Viewport applied later when the map instance is available (see effect below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  // 2. Apply saved viewport once the map exists.
  useEffect(() => {
    if (!enabled || !mapInstance) return
    const p = searchParams
    const centerStr = p.get('center')
    const zoomStr = p.get('zoom')
    if (centerStr && zoomStr) {
      const [lon, lat] = centerStr.split(',').map(Number)
      const zoom = parseFloat(zoomStr)
      if ([lon, lat, zoom].every(Number.isFinite)) {
        try { mapInstance.easeTo({ center: [lon, lat], zoom }) } catch { /* noop */ }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, mapInstance])

  // 3. WRITE state changes back to the URL (debounced by RAF).
  useEffect(() => {
    if (!enabled) return
    let rafId: number | null = null
    const write = () => {
      const { center, zoom } = readViewport(mapInstance)
      const q = buildExplorerSearch({
        collection: datasetId,
        variable: typeof datasetState.variable === 'string' ? datasetState.variable : undefined,
        qa: typeof datasetState.qaIndex === 'number' ? String(datasetState.qaIndex) : undefined,
        cmap: colormap,
        vmin: clim[0],
        vmax: clim[1],
        autoscale: timeMeanAutoClim,
        opacity,
        t: typeof datasetState.time === 'number' ? datasetState.time : undefined,
        center,
        zoom,
      })
      navigate({ search: q ? `?${q}` : '' }, { replace: true })
    }
    if (rafId !== null) cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(write)
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, datasetId, datasetState, clim, colormap, opacity, timeMeanAutoClim])

  // 4. Also write when the map viewport changes (moveend / zoomend).
  useEffect(() => {
    if (!enabled || !mapInstance) return
    let rafId: number | null = null
    const write = () => {
      const { center, zoom } = readViewport(mapInstance)
      const q = buildExplorerSearch({
        collection: datasetId,
        variable: typeof datasetState.variable === 'string' ? datasetState.variable : undefined,
        qa: typeof datasetState.qaIndex === 'number' ? String(datasetState.qaIndex) : undefined,
        cmap: colormap,
        vmin: clim[0],
        vmax: clim[1],
        autoscale: timeMeanAutoClim,
        opacity,
        t: typeof datasetState.time === 'number' ? datasetState.time : undefined,
        center,
        zoom,
      })
      navigate({ search: q ? `?${q}` : '' }, { replace: true })
    }
    const onMove = () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(write)
    }
    mapInstance.on?.('moveend', onMove)
    mapInstance.on?.('zoomend', onMove)
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      mapInstance.off?.('moveend', onMove)
      mapInstance.off?.('zoomend', onMove)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, mapInstance, datasetId, datasetState, clim, colormap, opacity, timeMeanAutoClim])
}
